/* LA SELEZIONE DA CONFRONTARE, NELL'URL.
 *
 * Sta nell'URL per la stessa ragione dei filtri (`statoRicerca.ts`): su una
 * lista da un milione e mezzo di righe, trovare le due configurazioni che
 * meritano un confronto costa fatica, e una selezione che sparisce al
 * ricaricamento è quella fatica buttata. Nell'URL invece si condivide, torna
 * indietro col tasto del browser e sopravvive a un aggiornamento di pagina.
 *
 * PERCHÉ NON DENTRO `StatoRicerca`. Quel modello dichiara di non essere più
 * ricco di quello che il server sa fare — ogni suo campo è un parametro reale
 * di `GET /backtester/search` — e la selezione non è un filtro: non cambia la
 * query. Metterla lì romperebbe quella corrispondenza. Vive quindi in un
 * parametro suo, e chi riscrive l'URL della ricerca deve ricordarsi di
 * riportarlo: `preserva` esiste apposta, perché `scriviInUrl` costruisce
 * l'indirizzo da zero e altrimenti cancellerebbe la selezione a ogni filtro
 * toccato.
 */

/**
 * Oltre quattro il confronto smette di essere «questo contro quello».
 *
 * Non è un limite tecnico: è che la tabella affiancata a cinque colonne su un
 * telefono non si legge, e soprattutto che con cinque candidati la domanda a
 * cui la pagina risponde — perché A è meglio di B — non ha più una forma. Chi
 * deve scremare venti righe lo fa con i filtri e l'ordinamento, non qui.
 */
export const TETTO_CONFRONTO = 4;

/** Il nome del parametro, uno solo, condiviso fra lista e pagina di confronto. */
export const PARAM_CONFRONTO = 'confronta';

/** Quanti candidati servono perché ci sia qualcosa da confrontare. */
export const MINIMO_CONFRONTO = 2;

/**
 * Gli identificativi selezionati, letti dall'URL.
 *
 * Scarta il non numerico e i doppioni invece di fidarsi: l'URL è scrivibile a
 * mano ed è la parte dell'applicazione che chiunque può alterare. Un id
 * ripetuto darebbe due colonne identiche e un «vincitore» fra una riga e sé
 * stessa.
 */
export function leggiSelezione(p: URLSearchParams): number[] {
  const grezzo = p.get(PARAM_CONFRONTO);
  if (!grezzo) return [];
  const visti = new Set<number>();
  const ids: number[] = [];
  for (const pezzo of grezzo.split(',')) {
    const n = Number(pezzo.trim());
    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) continue;
    if (visti.has(n)) continue;
    visti.add(n);
    ids.push(n);
    /* Il tetto si applica anche in lettura: un indirizzo con dieci id non deve
       poter aggirare il limite che l'interfaccia impone al tocco. */
    if (ids.length >= TETTO_CONFRONTO) break;
  }
  return ids;
}

/** Riporta la selezione dentro un indirizzo appena costruito da zero. */
export function preserva(p: URLSearchParams, ids: number[]): URLSearchParams {
  if (ids.length > 0) p.set(PARAM_CONFRONTO, ids.join(','));
  else p.delete(PARAM_CONFRONTO);
  return p;
}

/** L'esito di un tocco sulla casella: cosa diventa la selezione, e cosa dire. */
export interface EsitoCommutazione {
  ids: number[];
  /**
   * La frase da mostrare quando il tocco NON ha avuto effetto.
   *
   * `null` quando l'ha avuto. Esiste perché un clic ignorato in silenzio si
   * legge come un'interfaccia rotta: chi tocca la quinta casella deve sapere
   * che il confronto è pieno, non restare a chiedersi se ha sbagliato mira.
   */
  rifiutato: string | null;
}

export function commuta(ids: number[], id: number): EsitoCommutazione {
  if (ids.includes(id)) return { ids: ids.filter((x) => x !== id), rifiutato: null };
  if (ids.length >= TETTO_CONFRONTO) {
    return {
      ids,
      rifiutato: `Confronto pieno: ${TETTO_CONFRONTO} è il massimo. Togline uno per aggiungere questo.`,
    };
  }
  return { ids: [...ids, id], rifiutato: null };
}

/** L'indirizzo della pagina di confronto per una selezione. */
export function indirizzoConfronto(ids: number[]): string {
  return `/backtest/confronto?${PARAM_CONFRONTO}=${ids.join(',')}`;
}

/**
 * L'indirizzo della lista con un backtest già selezionato.
 *
 * È il percorso «Confronta con…» del dettaglio: da una riga sola non si può
 * confrontare niente, e spuntare due caselle in una lista da 2,3 milioni di
 * righe funziona se le righe sono vicine e non funziona affatto se non lo
 * sono. Si riparte quindi dalla lista, con questo già in mano e i filtri
 * liberi di cercare l'altro.
 */
export function indirizzoScegliAltro(id: number): string {
  return `/backtest?${PARAM_CONFRONTO}=${id}`;
}
